
import CustomerFooter from "@/components/customer-footer";
import CustomerScreenShell from "@/components/customer-screen-shell";
import Notice from "@buildpy/ui/notice";
import A4Fit from "@/modules/print/a4-fit";
import PrintButton from "@/modules/print/print-button";
import QuoteDocument from "@/modules/print/quote-document";
import { formatDate } from "@/modules/quote/ui/format";
import CustomerHeadOrg from "@/modules/share/ui/customer-head-org";
import { QuoteMobileSummary, QuoteMobileTrade, sortedTrades, tradeIndexOf } from "@/modules/share/ui/quote-mobile";
import ShareGate from "@/modules/share/ui/share-gate";
import type { CustomerQuoteView } from "@buildpy/core/views/g1b/customer-quote";

interface Props {
  data: CustomerQuoteView;
  token: string;
  t?: string;
}

/**
 * 고객이 보는 견적서. 로그인은 없고 토큰 + 휴대폰 뒷 4자리가 열쇠다.
 *
 * 화면은 셋 중 하나다 — 만료·회수 안내 / 뒷자리 입력 / 견적서.
 * 열람 기록(markViewed)은 인증 액션이 남긴다. 화면을 그릴 때마다 세면 조회수가 부푼다.
 */
const Page = ({ data: view, token, t }: Props) => {
  if (view.kind === "closed") {
    const { orgName } = view;
    return (
      <CustomerScreenShell>
        <Notice
          body={
            view.revoked
              ? `새 견적서가 발행되어 이 링크는 더 이상 열리지 않습니다. ${orgName}에 문의해 주세요.`
              : `${formatDate(view.expiresAt)}에 만료된 링크입니다. ${orgName}에 다시 요청해 주세요.`
          }
          title={view.revoked ? "회수된 링크입니다" : "만료된 링크입니다"}
        />
      </CustomerScreenShell>
    );
  }

  if (view.kind === "gate") {
    return (
      <CustomerScreenShell>
        <ShareGate orgName={view.orgName} token={token} />
      </CustomerScreenShell>
    );
  }

  const company = view.data.org;
  // 폰 뷰는 상세견적만 — 가견적은 개산서 한 장이라 폰에서도 A4 문서를 화면 폭에 맞게 줄여(A4Fit) 보인다.
  const phone = !view.data.rough;
  const tradeNo = phone ? tradeIndexOf(t, sortedTrades(view.data).length) : null;

  return (
    <CustomerScreenShell className="p-0">
      {phone && (
        <div className="print-hide pt-safe md:hidden" data-testid="quote-mobile">
          {tradeNo ? (
            <QuoteMobileTrade data={view.data} n={tradeNo} token={token} />
          ) : (
            <QuoteMobileSummary data={view.data} expiresAt={view.expiresAt} token={token} />
          )}
        </div>
      )}

      <div className={phone ? "hidden md:block print:block" : undefined} data-testid="quote-document-view">
        <header className="print-hide sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-4 pb-2.5 pt-safe-2.5">
          <CustomerHeadOrg name={company.name} tel={company.tel} />
          <PrintButton label="PDF 저장(인쇄)" variant="outline" />
        </header>

        <div className="print-root px-2 py-4 md:px-4">
          <A4Fit>
            <QuoteDocument data={view.data} />
          </A4Fit>
        </div>

        <footer className="print-hide px-4 pb-8 pt-2 text-center text-xs text-muted-foreground">
          링크 유효기간 {formatDate(view.expiresAt)}까지 · 문의 {company.name}
        </footer>
      </div>
      <CustomerFooter className="mt-0 px-4 pb-8" show={view.footer} />
    </CustomerScreenShell>
  );
};

export default Page;
